/** The admin's first screen: is the service healthy, and does anything need me?
 *
 * Five cards, and between them they answer both questions. Uploads and
 * storage are the service breathing — a week's shape, and where the bytes
 * went. Open issues and the last job run are the work: what the merge job did
 * at two in the morning, and what it could not decide without a person. The
 * streaming card is the only live thing on the page, and it is here because
 * an open socket is the one kind of load that cannot be read from a table
 * afterwards.
 *
 * Every number on the screen comes from one request. That is deliberate:
 * five cards fetching their own figures would draw five different instants of
 * the same service, and a dashboard whose pending count disagreed with its
 * issue count would be worse than no dashboard. The reply carries its own
 * `generated_at` and the page says when that was.
 *
 * The two buttons are the only things here that change anything. "Run job
 * now" is a dispatch to GitHub Actions rather than a start — the merge
 * happens in the track-data repository, not in this Worker — so the reply is
 * reported as what it is, including the case where no dispatch token is
 * configured and the run has merely been written down. Kicking a stream is
 * asked twice, because it is one click next to four read-only figures and
 * ends somebody's session.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminDashboard, kickLive, runJob, runSweep } from '@/lib/api';
import { bytes, count, DASH, duration, hz, money, plural, relativeTime } from '@/lib/format';
import type {
  AdminDashboard as Dashboard,
  LiveStream,
  Severity,
  StorageByType,
  SweepRun,
} from '@/lib/types';
import {
  Bar,
  Card,
  CardMeta,
  EmptyState,
  ErrorNote,
  Kicker,
  LoadingPanel,
  Page,
  PulseDot,
  Sparkline,
} from '@/components/ui';
import {
  AdminHeader,
  SEVERITY_ORDER,
  SeverityDot,
  invalidateAdminPulse,
  seedAdminPulse,
  ActivityFeed,
} from '@/components/admin';

/** How long "kick?" stays armed before it goes back to being a plain word. */
const CONFIRM_MS = 4000;

/** One of the four figures in the uploads card. The design gives each a size
 *  and a colour of its own — today is the headline, held is the worry — so
 *  they are spelled out rather than taken from the shared stat tile. A figure
 *  that names a filter — pending, held — is a link to the Uploads screen with
 *  that filter already on, because the number is the question and the table
 *  is the answer. */
function Figure({
  value,
  label,
  size = 22,
  colour = 'var(--color-neutral-300)',
  to,
}: {
  value: string;
  label: string;
  size?: number;
  colour?: string;
  to?: string;
}) {
  const body = (
    <>
      <div className="tabular-nums" style={{ fontSize: size, lineHeight: 1, color: colour }}>
        {value}
      </div>
      <div className="card-meta">{label}</div>
    </>
  );
  return to ? (
    <Link to={to} title={`open ${label} uploads`} style={{ color: 'inherit', textDecoration: 'none' }}>
      {body}
    </Link>
  ) : (
    <div>{body}</div>
  );
}

/** `02:00 UTC` — the job's own clock, as the header states it. */
function utcClock(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return DASH;
  const when = new Date(at);
  return `${String(when.getUTCHours()).padStart(2, '0')}:${String(when.getUTCMinutes()).padStart(2, '0')} UTC`;
}

/** What a storage row counts in. Each type measures itself differently and
 *  the sentence should say which. */
/** What a sweep removed, as the few words worth reading: nothing that is
 *  zero, and the table's name in the admin's terms rather than the schema's. */
function sweepRemoved(run: SweepRun): string {
  const { summary } = run;
  const parts: [number, string][] = [
    [summary.logs, 'log line'],
    [summary.drive_sessions, 'expired session'],
    [summary.laps, 'lap'],
    [summary.lap_documents ?? 0, 'lap recording'],
    [summary.uploads_expired, 'held upload'],
    [summary.live_streams, 'stale stream'],
    [summary.recordings, 'recording'],
    [summary.web_sessions + summary.oauth_states, 'expired sign-in'],
    [summary.rate_events, 'rate-limit event'],
  ];
  const said = parts.filter(([n]) => n > 0).map(([n, word]) => plural(n, word));
  const kept = summary.kept_for_best ?? 0;
  const keptNote = kept > 0 ? `; kept ${plural(kept, 'session')} for a personal best` : '';
  return said.length > 0
    ? `removed ${said.join(', ')}${keptNote}`
    : `nothing was past its expiry${keptNote}`;
}

function storageCount(row: StorageByType): string {
  if (row.sessions !== undefined) return plural(row.sessions, 'session');
  if (row.uploads !== undefined) return plural(row.uploads, 'upload');
  if (row.streams !== undefined) return plural(row.streams, 'stream');
  return plural(row.objects, 'object');
}

export default function AdminDashboard() {
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [jobBusy, setJobBusy] = useState(false);
  const [jobNote, setJobNote] = useState<string | null>(null);
  const [jobError, setJobError] = useState<unknown>(null);
  /** Null while the "One track…" field is closed. */
  const [oneTrack, setOneTrack] = useState<string | null>(null);

  const [sweepBusy, setSweepBusy] = useState(false);
  const [sweepRun, setSweepRun] = useState<SweepRun | null>(null);
  const [sweepError, setSweepError] = useState<unknown>(null);

  const [arming, setArming] = useState<string | null>(null);
  const [kickError, setKickError] = useState<unknown>(null);
  const armTimer = useRef<number | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (armTimer.current !== null) window.clearTimeout(armTimer.current);
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const reply = await adminDashboard();
      if (!alive.current) return;
      setData(reply);
      setError(null);
      // The header wants two of these figures; it should not ask for the
      // whole document again to get them.
      seedAdminPulse(reply);
    } catch (thrown) {
      if (alive.current) setError(thrown);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const dispatchJob = useCallback(
    async (officialId?: string) => {
      setJobBusy(true);
      setJobError(null);
      setJobNote(null);
      try {
        const started = await runJob(officialId ? { official_id: officialId } : {});
        if (!alive.current) return;
        setJobNote(
          started.dispatched
            ? `dispatched to ${started.repository} · ${started.scope}`
            : started.reason,
        );
        setOneTrack(null);
        invalidateAdminPulse();
        await load();
      } catch (thrown) {
        if (alive.current) setJobError(thrown);
      } finally {
        if (alive.current) setJobBusy(false);
      }
    },
    [load],
  );

  const dispatchSweep = useCallback(async () => {
    setSweepBusy(true);
    setSweepError(null);
    try {
      const ran = await runSweep();
      if (!alive.current) return;
      setSweepRun(ran);
      // A sweep moves the figures on this page: stale streams, held uploads.
      invalidateAdminPulse();
      await load();
    } catch (thrown) {
      if (alive.current) setSweepError(thrown);
    } finally {
      if (alive.current) setSweepBusy(false);
    }
  }, [load]);

  const armKick = useCallback((userId: string) => {
    setKickError(null);
    setArming(userId);
    if (armTimer.current !== null) window.clearTimeout(armTimer.current);
    armTimer.current = window.setTimeout(() => {
      if (alive.current) setArming(null);
    }, CONFIRM_MS);
  }, []);

  const doKick = useCallback(
    async (stream: LiveStream) => {
      setArming(null);
      if (armTimer.current !== null) window.clearTimeout(armTimer.current);
      try {
        await kickLive(stream.user_id);
        await load();
      } catch (thrown) {
        if (alive.current) setKickError(thrown);
      }
    },
    [load],
  );

  if (loading && !data) {
    return (
      <Page max={1300}>
        <AdminHeader />
        <LoadingPanel label="loading the dashboard" />
      </Page>
    );
  }

  if (!data) {
    return (
      <Page max={1300}>
        <AdminHeader />
        <div style={{ marginTop: 18 }}>
          <ErrorNote
            error={error}
            title="the dashboard could not be read"
            onRetry={() => {
              setLoading(true);
              void load();
            }}
          />
        </div>
      </Page>
    );
  }

  const { uploads, daily, issues, last_job_run: lastRun, live, storage } = data;
  const severities: Record<Severity, number> = {
    error: issues.open.error,
    warn: issues.open.warn,
    info: issues.open.info,
  };

  return (
    <Page max={1300} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <AdminHeader openIssues={issues.open.total} lastRun={lastRun} />

      {/* A stale read is still worth drawing; the failure goes above it. */}
      {error ? <ErrorNote error={error} title="this is the last reading" compact /> : null}

      <section aria-label="Service overview" className="grid grid-cols-1 gap-3 lg:grid-cols-12">
        {/* ── uploads ───────────────────────────────────────────────── */}
        <Card elevation="none" className="lg:col-span-5" label="Uploads" as="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Kicker>uploads</Kicker>
            <CardMeta>last 7 days</CardMeta>
          </div>
          <div style={{ display: 'flex', gap: 28, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Figure value={count(uploads.today)} label="today" size={34} colour="var(--color-text)" />
            <Figure value={count(uploads.week)} label="this week" />
            <Figure value={count(uploads.pending)} label="pending" to="/admin/uploads?status=pending" />
            <Figure
              value={count(uploads.held)}
              label="held"
              colour="var(--color-warn)"
              to="/admin/uploads?status=held"
            />
          </div>
          <Sparkline
            values={daily.map((day) => day.uploads)}
            labels={daily.map((day) => day.weekday)}
            markLast
            label={`uploads per day: ${daily.map((day) => `${day.weekday} ${day.uploads}`).join(', ')}`}
            style={{ marginTop: 6 }}
          />
        </Card>

        {/* ── open issues ───────────────────────────────────────────── */}
        <Card elevation="none" className="lg:col-span-3" label="Open issues" as="section">
          <Kicker>open issues</Kicker>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
            {SEVERITY_ORDER.map((severity) => (
              <Link
                key={severity}
                to={`/admin/issues?severity=${severity}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 8,
                  padding: '4px 6px',
                  margin: '0 -6px',
                  borderRadius: 'var(--radius-sm)',
                  color: 'inherit',
                  textDecoration: 'none',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <SeverityDot severity={severity} />
                  {severity}
                </span>
                <span className="tabular-nums" style={{ fontSize: 22, lineHeight: 1 }}>
                  {count(severities[severity])}
                </span>
              </Link>
            ))}
          </div>
          <div className="card-meta" style={{ marginTop: 'auto' }}>
            <Link to="/admin/issues" style={{ color: 'inherit' }}>
              error issues raise a webhook alert · open inbox →
            </Link>
          </div>
        </Card>

        {/* ── last job run ──────────────────────────────────────────── */}
        <Card elevation="none" className="lg:col-span-4" label="Last job run" as="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <Kicker>last job run</Kicker>
            <CardMeta className="tabular-nums">
              {lastRun
                ? `${utcClock(lastRun.started_at)}${
                    lastRun.duration_s === null ? '' : ` · ${duration(lastRun.duration_s * 1000)}`
                  }`
                : 'never'}
            </CardMeta>
          </div>

          {lastRun ? (
            <div
              className="tabular-nums"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: '4px 12px',
                fontSize: 12.5,
              }}
            >
              <span style={{ color: 'var(--color-neutral-500)' }}>tracks touched</span>
              <span>{count(lastRun.tracks_touched)}</span>
              <span style={{ color: 'var(--color-neutral-500)' }}>PRs opened</span>
              <span>{count(lastRun.prs_opened)}</span>
              <span style={{ color: 'var(--color-neutral-500)' }}>auto-merged</span>
              <span style={{ color: 'var(--color-ok)' }}>{count(lastRun.auto_merged)}</span>
              <span style={{ color: 'var(--color-neutral-500)' }}>awaiting review</span>
              <span style={{ color: 'var(--color-warn)' }}>{count(lastRun.awaiting_review)}</span>
              <span style={{ color: 'var(--color-neutral-500)' }}>issues raised</span>
              <span>{count(lastRun.issues_raised)}</span>
            </div>
          ) : (
            <p className="card-body" style={{ color: 'var(--color-neutral-500)' }}>
              The merge job has not reported a run to this service yet.
            </p>
          )}

          {jobNote ? (
            <p
              style={{
                margin: 0,
                fontSize: 11.5,
                color: 'var(--color-neutral-500)',
                textWrap: 'pretty',
              }}
            >
              {jobNote}
            </p>
          ) : null}
          {jobError ? <ErrorNote error={jobError} title="the job was not started" compact /> : null}

          <div style={{ marginTop: 'auto' }}>
            {oneTrack === null ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ fontSize: 12 }}
                  disabled={jobBusy}
                  onClick={() => void dispatchJob()}
                >
                  {jobBusy ? 'Running…' : 'Run job now'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: 12 }}
                  disabled={jobBusy}
                  onClick={() => setOneTrack('')}
                >
                  One track…
                </button>
              </div>
            ) : (
              <form
                style={{ display: 'flex', gap: 6, alignItems: 'flex-end', flexWrap: 'wrap' }}
                onSubmit={(event) => {
                  event.preventDefault();
                  const track = oneTrack.trim();
                  if (track) void dispatchJob(track);
                }}
              >
                <div className="field" style={{ flex: '1 1 160px', minWidth: 0 }}>
                  <label htmlFor="job-one-track">circuit</label>
                  <input
                    id="job-one-track"
                    className="input"
                    // The field did not exist a moment ago; the click that
                    // revealed it was a request to type in it.
                    autoFocus
                    value={oneTrack}
                    placeholder="official_id"
                    style={{ minHeight: 30, fontSize: 13 }}
                    onChange={(event) => setOneTrack(event.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ fontSize: 12 }}
                  disabled={jobBusy || oneTrack.trim() === ''}
                >
                  Run
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ fontSize: 12, color: 'var(--color-neutral-500)' }}
                  onClick={() => setOneTrack(null)}
                >
                  Cancel
                </button>
              </form>
            )}
          </div>
        </Card>

        {/* ── streaming now ─────────────────────────────────────────── */}
        <Card elevation="none" className="lg:col-span-12" label="What happened" as="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <Kicker>what happened</Kicker>
            <CardMeta>
              <Link to="/admin/logs">everything, with filters</Link>
            </CardMeta>
          </div>
          <ActivityFeed compact limit={8} />
        </Card>

        <Card elevation="none" className="lg:col-span-5" label="Streaming now" as="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <Kicker>streaming now</Kicker>
            <CardMeta>
              {plural(live.drivers, 'driver')} · {plural(live.spectators, 'spectator')} ·{' '}
              <Link to="/admin/live" style={{ color: 'inherit' }}>
                open live →
              </Link>
            </CardMeta>
          </div>

          {kickError ? <ErrorNote error={kickError} title="the stream was not ended" compact /> : null}

          {live.streams.length === 0 ? (
            <EmptyState compact title="nobody is streaming">
              A logger with <code>live:write</code> opens one socket per session; the card fills as
              soon as one does.
            </EmptyState>
          ) : (
            <div>
              {live.streams.map((stream) => (
                <div
                  key={stream.user_id}
                  className="tabular-nums"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0,1fr) auto auto auto',
                    gap: 14,
                    alignItems: 'center',
                    fontSize: 13,
                    padding: '6px 0',
                    opacity: stream.stale ? 0.55 : 1,
                    background: 'var(--row-rule) no-repeat bottom / 100% 1px',
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <PulseDot size={6} tone={stream.stale ? 'neutral' : 'accent'} pulse={!stream.stale} />
                    <Link
                      to={`/live/${encodeURIComponent(stream.display_name)}`}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      {stream.display_name}
                    </Link>
                    <span style={{ color: 'var(--color-neutral-500)' }}>
                      {stream.track_name || stream.official_id}
                    </span>
                  </span>
                  <span style={{ color: 'var(--color-neutral-400)', whiteSpace: 'nowrap' }}>
                    {stream.stale ? relativeTime(stream.last_frame_at) : hz(stream.hz)}
                  </span>
                  <span style={{ color: 'var(--color-neutral-400)', whiteSpace: 'nowrap' }}>
                    {count(stream.spectators)} watching
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{
                      fontSize: 11,
                      color:
                        arming === stream.user_id
                          ? 'var(--color-error)'
                          : 'var(--color-neutral-500)',
                    }}
                    onClick={() =>
                      arming === stream.user_id ? void doKick(stream) : armKick(stream.user_id)
                    }
                  >
                    {arming === stream.user_id ? 'kick — confirm' : 'kick'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* ── storage by type ───────────────────────────────────────── */}
        <Card elevation="none" className="lg:col-span-7" label="Storage by type" as="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <Kicker>storage by type</Kicker>
            <CardMeta className="tabular-nums">
              R2 · {bytes(storage.total_bytes)} ·{' '}
              <Link to="/admin/sessions" style={{ color: 'inherit' }}>
                by account →
              </Link>
            </CardMeta>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 2 }}>
            {storage.by_type.map((row) => (
              <div key={row.type}>
                <div
                  className="tabular-nums"
                  style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12.5 }}
                >
                  <span style={{ minWidth: 0 }}>
                    {row.type}{' '}
                    <span style={{ color: 'var(--color-neutral-500)' }}>
                      · {row.retention} · {storageCount(row)}
                    </span>
                  </span>
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {row.bytes > 0 ? bytes(row.bytes) : DASH}
                  </span>
                </div>
                <Bar
                  value={row.share_pct}
                  height={4}
                  label={`${row.type}: ${row.share_pct}% of stored bytes`}
                  style={{ marginTop: 5 }}
                />
              </div>
            ))}
          </div>
        </Card>

        {/* ── nightly sweep ─────────────────────────────────────────── */}
        <Card elevation="none" className="lg:col-span-12" label="Nightly sweep" as="section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <Kicker>nightly sweep</Kicker>
            <CardMeta className="tabular-nums">03:17 UTC, every night</CardMeta>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '10px 16px',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {sweepRun ? (
                <>
                  <p className="tabular-nums" style={{ margin: 0, fontSize: 12.5, textWrap: 'pretty' }}>
                    ran in {duration(sweepRun.ms)} · {sweepRemoved(sweepRun)}
                  </p>
                  <p
                    className="tabular-nums"
                    style={{ margin: 0, fontSize: 11.5, color: 'var(--color-neutral-500)', textWrap: 'pretty' }}
                  >
                    {sweepRun.cost_summary
                      ? `public costs summary · ${sweepRun.cost_summary.month} · ${money(
                          sweepRun.cost_summary.estimate_usd,
                        )} estimated · ${money(sweepRun.cost_summary.covered_usd)} covered${
                          sweepRun.cost_summary.covered_through
                            ? ` · covered through ${sweepRun.cost_summary.covered_through}`
                            : ''
                        }`
                      : 'the public costs summary was not written'}
                  </p>
                  {sweepRun.summary.failed.length > 0 ? (
                    <p style={{ margin: 0, fontSize: 11.5, color: 'var(--color-error)' }}>
                      failed: {sweepRun.summary.failed.join(', ')} — the Logs screen has the reason
                    </p>
                  ) : null}
                  {sweepRun.summary.behind.length > 0 ? (
                    <p style={{ margin: 0, fontSize: 11.5, color: 'var(--color-warn)' }}>
                      still behind: {sweepRun.summary.behind.join(', ')} — run it again
                    </p>
                  ) : null}
                </>
              ) : (
                <p className="card-body" style={{ margin: 0, color: 'var(--color-neutral-500)', textWrap: 'pretty' }}>
                  Deletes what is past its retention — sessions, logs, stale streams, held uploads —
                  and rewrites the public costs summary the landing page reads. Running it now does
                  exactly what tonight’s run would.
                </p>
              )}
              {sweepError ? <ErrorNote error={sweepError} title="the sweep did not run" compact /> : null}
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 12 }}
              disabled={sweepBusy}
              onClick={() => void dispatchSweep()}
            >
              {sweepBusy ? 'Sweeping…' : 'Run sweep now'}
            </button>
          </div>
        </Card>
      </section>

      <p style={{ margin: 0, fontSize: 11, color: 'var(--color-neutral-600)' }}>
        counted {relativeTime(data.generated_at)}
      </p>
    </Page>
  );
}
